import {IRange} from '../functions-old/ranges';
import {superSafeURLDecode, superSafeURLEncode} from '../utils-old/formatting';
import {Timestamp} from '@angular/fire/firestore';
import type {FieldValue} from 'firebase/firestore';
import {PropertyToTitle} from '../../shared/shared-utils/conversions/properties';
import {IGlobalError} from '../../shared/shared-components/components/global-error-display/models/global-error';

export type ValueOf<T> = T[keyof T];

export type Feature = 'shared' | 'operational' | 'observation';

export interface StoreInfo {
  name: string;
  keyValueSettings?: { linkDep: boolean };
}

export type VatRates = Record<number, { desc: string; vatRate: number }>;

export interface SubDep {
  dep: string | string[];
  name: string;
  targetGP: number;
}

export interface Colleague {
  stores: Record<string, string>;
  name?: string;
}

export const OUT_GOING_MSG_TYPES =
  ['SERVER_TEST', 'STOCK_UPDATE', 'SERVER_CONFIG', 'AUTO_ORDERS', 'AUTO_ORDERS_U_UPDATE'] as const;
export const INCOMING_MSG_TYPES = ['STOCK_UPDATE', 'AUTO_ORDER_RESULT', 'SIGN_UP', 'ACCESS_CHANGE'] as const;
export const MESSAGE_TYPES = [...OUT_GOING_MSG_TYPES, ...INCOMING_MSG_TYPES] as const;

export type OutGoingMsgType = typeof OUT_GOING_MSG_TYPES[number];
export type IncomingMsgType = typeof INCOMING_MSG_TYPES[number];

export type MsgType = OutGoingMsgType | IncomingMsgType;

/** Stored shape after read; writes may use Firestore `serverTimestamp()`. */
export type IOperationalEventTimestamp =
  | Date
  | Timestamp
  | { seconds: number; nanoseconds: number }
  | FieldValue;

export interface Message<TPayloadData> {
  payload: {
    body?: string;
    data?: TPayloadData;
    event?: string;
    success?: boolean;
  };
  sender: string;
  timestamp: IOperationalEventTimestamp;
  type: MsgType;
  id?: string;
  nb?: boolean;
  newStockItem?: boolean;
  reverted?: boolean;
}

export interface INotification {
  id: string;
  response: string;
  storeId: string;
  success: boolean;
  timestamp: { seconds: number, nanoseconds: number };
  type: MsgType;
  event?: string;
  nb?: boolean;
}

/** Values allowed on StockItemUpdateObj `o` / `n` (includes arrays e.g. multipleBarcodes). */
export type StockItemUpdateFieldValue = string | number | Date | boolean | string[];

export interface StockItemUpdateObj {
  n: Record<string, StockItemUpdateFieldValue>;
  o: Record<string, StockItemUpdateFieldValue>;
  details?: Record<string | number, string | number | Date | boolean>;
}

export interface ApiLogObj2 {
  creationDate: IOperationalEventTimestamp;
  data: Record<string, StockItemUpdateObj>;
  type: 'STOCK_UPDATE';
  userId: string;
  editedItems?: string[];
  errors?: (Error | IGlobalError)[];
  executedDate?: Date;
  id?: string;
  lastAttempt?: Date;
  message?: { original: string; new: string };
  newStockItem?: boolean;
  reverted?: boolean;
  scheduleDate?: Date;
  status?: 'SUBMITTED' | 'COMPLETE' | 'FAILED' | 'REATTEMPTED';
  taggedItems?: Record<string, { o?: string[]; n?: string[] } | string[]>;
}

export interface DepSales {
  grossProfitMTD: number;
  grossProfitPMTD: number;
  grossProfitPYTD: number;
  grossProfitToday: number;
  grossProfitYTD: number;
  name: string;
  salesMTD: number;
  salesPMTD: number;
  salesPYTD: number;
  salesToday: number;
  salesYTD: number;
}

export interface DepSales2 {
  customerCount: number;
  netCost: number;
  netSales: number;
}

export interface DepSalesHistory {
  byDep: Record<string, Map<number, Map<number, Map<number, DepSales2>>>>;
  byYear: Map<number, Map<number, Map<number, Record<string, DepSales2>>>>;
  totals?: Map<number, Map<number, Map<number, DepSales2>>>;
}

export interface Supplier {
  account: string;
  adr1: string;
  adr2: string;
  adr3: string;
  adr4: string;
  age: number;
  cCB: number;
  cell: string;
  contact: string;
  country: string;
  crLimit: number;
  current: number;
  days120: number;
  days150: number;
  days180: number;
  days30: number;
  days60: number;
  days90: number;
  dlv1: string;
  dlv2: string;
  dlv3: string;
  dlv4: string;
  dlvPostC: string;
  email: string;
  linkAcc: string;
  name: string;
  onHold: boolean | string;
  postC: string;
  taxNo: string;
  tel1: string;
  tel2: string;
  terms: number;
  total: number;
  addEmails?: string[];
  emailNames?: Record<string, string>;
  minOrder?: number;
  onHoldCode?: number;
}

// -------------------------------------------------- STOCK ITEMS --------------------------------------------------- //

export interface DisabledRule {
  descPrefix?: string;
  descPrefixArray?: string[];
  descSuffix?: string;
  descSuffixArray?: string[];
  lineColour?: number;
  onHoldCode?: number;
  regularSuppCode?: string;
  supplierAction?: 'none' | 'remove' | 'set';
}

export type DisabledRuleSupp = Omit<DisabledRule, 'lineColour'>;

export const disableRuleInvolvedKeys = (dr: DisabledRules | DisabledRule): string[] => {
  let keys: string[] = [];

  if (dr.hasOwnProperty('onHandZero') || dr.hasOwnProperty('onHandNotZero')) {
    dr = dr as DisabledRules;
    if (dr.onHandZero) {
      keys = disableRuleInvolvedKeys(dr.onHandZero);
    }
    if (dr.onHandNotZero) {
      keys = keys.concat(disableRuleInvolvedKeys(dr.onHandNotZero));
    }
    keys = [...new Set(keys)];
  } else {
    dr = dr as DisabledRule;

    if (dr.hasOwnProperty('descPrefix') && dr.descPrefix !== null) {
      keys.push('desc');
    } else if (dr.hasOwnProperty('descSuffix') && dr.descSuffix !== null) {
      keys.push('desc');
    }
    if (dr.hasOwnProperty('onHoldCode') && dr.onHoldCode !== null) {
      keys.push('onHoldCode');
    }
    if (dr.hasOwnProperty('lineColour') && dr.lineColour !== null) {
      keys.push('lineColourCode');
    }
    if (dr.hasOwnProperty('supplierAction') && dr.supplierAction && dr.supplierAction !== 'none') {
      keys.push('regularSuppCode');
    }
  }
  return keys;
};

export interface DisabledRules {
  onHandNotZero?: DisabledRule;
  onHandZero?: DisabledRule;
}

export interface DisabledRulesSupp extends DisabledRules {
  onHandNotZero?: DisabledRuleSupp;
  onHandZero?: DisabledRuleSupp;
}

export interface PriceBand {
  centSnaps?: { range: IRange; v: number }[];
  departs?: string[];
  randSnap?: { digits: number; snaps: { range: IRange; v: number }[] };
  range?: IRange;
  subDeparts?: string[];
}

export interface StockValChangeCheckConfig {
  desc?: SVCCNumber;
  sellPriIncl1?: SVCCNumber;
}

export interface StockValChangeCheckResult {
  new: string | number | Date;
  thresh: boolean | number;
  type: keyof SVCCNumber;
  desc?: string;
  old?: string | number | Date;
}

export type StockValuesChangeCheckResult = { [k in keyof StockValChangeCheckConfig]?: StockValChangeCheckResult[] };

export interface SVCCNumber {
  negPct?: number;
  noNeg?: boolean;
  noZero?: boolean;
  pct?: number;
  test?: string;
}

export const stockValCCRType2Str = (
  t: keyof SVCCNumber, threshold: number | boolean, key?: keyof StockValChangeCheckConfig,
): string => {
  const valStr = key && keyToTitle[key] ? keyToTitle[key] : null;

  switch (t) {
    case 'pct':
      return `${valStr}s have increased by more than ${threshold as number * 100}%`;
    case 'negPct':
      // Assuming a negative percentage change
      return `${valStr}s have decreased by more than ${threshold as number * 100}%`;
    // Add other cases as needed
    case 'noZero':
      // are zero and zero is not allowed
      return `${valStr} cannot be zero`;
    case 'noNeg':
      // are negative and negative is not allowed
      return `${valStr} cannot be negative`;
    case 'test':
      // are negative and negative is not allowed
      return ` ${valStr} test Change`;
    default:
      return null;
  }
};

export const stockValCCR2Str = (svccr: StockValChangeCheckResult): string => {
  const value = svccr.new as number;
  const original = svccr.old as number;

  const numberChange = (): number => (value - original) / original;

  switch (svccr.type) {
    case 'pct':
      return `Value ${value} is ${(numberChange() * 100).toFixed(2)} larger then current value of ` +
        `${original}. Threshold is ${((svccr.thresh as number) * 100).toFixed(2)}`;
    case 'negPct':
      return `Value ${value} is ${Math.abs(numberChange() * 100).toFixed(2)} smaller then current value of ` +
        `${original}. Threshold is ${((svccr.thresh as number) * 100).toFixed(2)}`;
    case 'noZero':
      return 'Value should not be zero.';
    case 'noNeg':
      return 'Value should not be negative';
    case 'test':
      return 'the testing test test should test the testing test';
    default:
      return null;
  }
};

export const hasValue = <T>(v: T | null | true): T | false => {
  return v !== null && v !== true ? v : false;
};

export interface Filter {
  condition: '=' | '<' | '>' | '<=' | '>=' | '!=' | 'in' | 'TO';
  itemIntKey: string;
  value: string | number | Date | string[] | number[];
  type?: 'search' | 'query';
}

export const filterConditions = (type: 'search' | 'query' = 'search'): string[] => {
  if (type === 'search') {
    return ['=', '<', '>', '<=', '>=', '!=', 'TO'];
  } else if (type === 'query') {
    return ['=', '<', '>', '<=', '>=', '!=', 'in'];
  }
  return null;
};

export interface StockItem {
  avCost: number;
  barcode: string;
  binL: string;
  category: number;
  code: string;
  created: Date;
  dep: string;
  desc: string;
  discExempt: boolean;
  dlvColl: number;
  excludeSellingValue: number;
  genCode: string;
  gp1: number;
  lastMoved: Date;
  lastPurchase: Date;
  lastSold: Date;
  lastStockTake: Date;
  latestCost: number;
  lineColourCode: number;
  maxDisc: number;
  modified: Date;
  noDecimal: boolean;
  onHand: number;
  onHoldCode: number;
  ordLvl: number;
  packSize: number;
  prvSellingPri: number;
  prvSellingPriDate: Date;
  purchaseOrder: number;
  recommendedGP: number;
  regularSuppCode: string;
  reportItemCode: string;
  reportItemFactor: number;
  salesOrder: number;
  sellIntoNegative: boolean;
  sellPriExcl1: number;
  sellPriIncl1: number;
  sellUnderCost: boolean;
  subDep: string;
  suppCode: string;
  suppUsedLast1: string;
  suppUsedLast2: string;
  suppUsedLast3: string;
  suppUsedLast4: string;
  suppUsedLast5: string;
  suppUsedLastDate1: Date;
  suppUsedLastDate2: Date;
  suppUsedLastDate3: Date;
  suppUsedLastDate4: Date;
  suppUsedLastDate5: Date;
  suppUsedLastPrice1: string;
  suppUsedLastPrice2: string;
  suppUsedLastPrice3: string;
  suppUsedLastPrice4: string;
  suppUsedLastPrice5: string;
  unitsYear: number;
  user: number;
  vatR: number;
  web: boolean;
  _tags?: string[];
  alternateDescription?: string;
  depName?: string;
  diffGP?: number;
  disabled?: boolean;
  errorType?: string[];
  isEdited?: boolean;
  isError?: boolean;
  isLoading?: boolean;
  isModalShown?: boolean;
  isSubmitted?: boolean;
  lineColourDescription?: string;
  lineColourValue?: string;
  maxOrderLevel?: number;
  minOrderLevel?: number;
  multipleBarcodes?: string[];
  multipleSuppliers?: string[];
  newlyAdded?: boolean;
  nominalGP?: number;
  orderQty?: number;
  orderQuantity?: number;
  originalValue?: Record<string, { value: boolean | number | string | Date }>;
  prevCostPrice?: number;
  prevGP?: number;
  sellPrice10?: number;
  sellPrice2?: number;
  sellPrice3?: number;
  sellPrice4?: number;
  sellPrice5?: number;
  sellPrice6?: number;
  sellPrice7?: number;
  sellPrice8?: number;
  sellPrice9?: number;
  sellPriceIncluding10?: number;
  sellPriceIncluding2?: number;
  sellPriceIncluding3?: number;
  sellPriceIncluding4?: number;
  sellPriceIncluding5?: number;
  sellPriceIncluding6?: number;
  sellPriceIncluding7?: number;
  sellPriceIncluding8?: number;
  sellPriceIncluding9?: number;
  stockId?: string;
  stockValue?: number;
  store?: string;
  storeId?: string;
  subDepName?: string;
  suppName?: string;
  supplierDetails?: { supplier: string; stockCode: string }[];
}

export interface IAutoOrderStockItem extends StockItem {
  unitsMonthAvg?: number;
}

export const stockCodeEncode = superSafeURLEncode;
export const stockCodeDecode = superSafeURLDecode;

export interface StockItemLocallyExtended extends StockItem {
  nominalGP?: number; // This is currently for gp editing. As gp1 is on average cost, not nominal. So no good
  storeId?: string;
}

export interface StockItemUnsafeAdditions extends StockItem {
  link?: string;
  multipleBarcodes?: string[],
  multipleSuppliers?: string[],
  supplierDetails?: { supplier: string; stockCode: string }[];
}

export type RawStockItem = Record<ValueOf<typeof sItemKeyToInt>, string | number | boolean | Timestamp>;

export const sItemKeyToInt: { [k in keyof StockItemUnsafeAdditions] } = {
  stockId: 'objectID',
  code: '0',
  desc: '1',
  suppCode: '2',
  dep: '3',
  subDep: '4',
  category: '5',
  lastMoved: '6',
  lastSold: '7',
  lastPurchase: '8',
  prvSellingPri: '9',
  prvSellingPriDate: '10',
  sellPriExcl1: '11',
  sellPriIncl1: '12',
  onHand: '13',
  avCost: '14',
  latestCost: '15',
  gp1: '16',
  recommendedGP: '17',
  suppUsedLast5: '18',
  suppUsedLast4: '19',
  suppUsedLast3: '20',
  suppUsedLast2: '21',
  suppUsedLast1: '22',
  suppUsedLastPrice5: '23',
  suppUsedLastPrice4: '24',
  suppUsedLastPrice3: '25',
  suppUsedLastPrice2: '26',
  suppUsedLastPrice1: '27',
  suppUsedLastDate5: '28',
  suppUsedLastDate4: '29',
  suppUsedLastDate3: '30',
  suppUsedLastDate2: '31',
  suppUsedLastDate1: '32',
  salesOrder: '33',
  purchaseOrder: '34',
  dlvColl: '35',
  vatR: '36',
  reportItemCode: '37',
  reportItemFactor: '38',
  onHoldCode: '39',
  regularSuppCode: '40',
  created: '41',
  user: '42',
  discExempt: '43',
  maxDisc: '44',
  noDecimal: '45',
  lineColourCode: '46',
  lastStockTake: '_stock_take_date',
  stockValue: '_stock_value',
  web: '48',
  sellUnderCost: '49',
  sellIntoNegative: '50',
  packSize: '51',
  barcode: '52',
  binL: '53',
  ordLvl: '54',
  genCode: '55',
  excludeSellingValue: '56',
  minOrderLevel: '57',
  maxOrderLevel: '58',
  modified: '59',
  sellPrice2: '60',
  sellPriceIncluding2: '61',
  sellPrice3: '64',
  sellPriceIncluding3: '65',
  sellPrice4: '66',
  sellPriceIncluding4: '67',
  sellPrice5: '68',
  sellPriceIncluding5: '69',
  sellPrice6: '70',
  sellPriceIncluding6: '71',
  sellPrice7: '72',
  sellPriceIncluding7: '73',
  sellPrice8: '74',
  sellPriceIncluding8: '75',
  sellPrice9: '76',
  sellPriceIncluding9: '77',
  sellPrice10: '78',
  sellPriceIncluding10: '79',
  orderQty: '62',
  alternateDescription: '63',
  unitsYear: 'units',
  link: 'link',
  _tags: '_tags',
  multipleBarcodes: 'multipleBarcodes',
  multipleSuppliers: 'multipleSuppliers',
  supplierDetails: 'supplierDetails',
} as const;

export const editableKeys = [
  'desc',
  'alternateDescription',
  'barcode',
  'binL',
  'dep',
  'subDep',
  'genCode',
  'onHoldCode',
  '_tags',
  'ordLvl',
  'orderQuantity',
  'orderQty',
  'maxOrderLevel',
  'minOrderLevel',
  'packSize',
  'sellPriIncl1',
  'sellPriExcl1',
  'sellPrice2',
  'sellPriceIncluding2',
  'sellPrice3',
  'sellPriceIncluding3',
  'sellPrice4',
  'sellPriceIncluding4',
  'sellPrice5',
  'sellPriceIncluding5',
  'sellPrice6',
  'sellPriceIncluding6',
  'sellPrice7',
  'sellPriceIncluding7',
  'sellPrice8',
  'sellPriceIncluding8',
  'sellPrice9',
  'sellPriceIncluding9',
  'sellPrice10',
  'sellPriceIncluding10',
  'recommendedGP',
  'latestCost',
  'suppCode',
  'regularSuppCode',
  'multipleBarcodes',
  'multipleSuppliers',
  'lineColourCode',
  'vatR',
];

/**
 * Text stock fields that may be saved as an empty string instead of reverting to their previous value.
 */
export const clearableTextKeys: string[] = [
  'alternateDescription',
  'barcode',
];

/**
 * Returns true when two clearable text values represent the same content.
 * Missing/null and '' are equivalent because source documents often omit unset fields.
 */
export function clearableTextValuesAreEqual(firstValue: unknown, secondValue: unknown): boolean {
  return String(firstValue ?? '') === String(secondValue ?? '');
}

export const sItemIntToKey: Record<ValueOf<typeof sItemKeyToInt>, keyof StockItem> = {};
for (const i of Object.keys(sItemKeyToInt)) {
  sItemIntToKey[sItemKeyToInt[i]] = i as keyof StockItem;
}

export const castStockItem = (rawItem: RawStockItem, castKeys: 'ALL' | (keyof StockItem)[] = 'ALL'): StockItem => {
  const item = {};
  let keys: (keyof RawStockItem)[];

  if (castKeys === 'ALL') {
    keys = Object.keys(rawItem) as (keyof RawStockItem)[];
  } else {
    keys = castKeys.map((k) => sItemKeyToInt[k]);
  }
  keys.forEach((key: string) => {
    if (keyToType[sItemIntToKey[key]] === 'date' && rawItem[key]) {
      item[sItemIntToKey[key]] = (rawItem[key] as Timestamp).toDate();
    } else {
      item[sItemIntToKey[key]] = rawItem[key];
    }
  });
  return item as StockItem;
};

export const castFireStockItem = (item: StockItem): {} => {
  const rawItem = {};
  Object.keys(item).forEach((key: string) => {
    rawItem[sItemKeyToInt[key]] = item[key];
  });
  return rawItem;
};

export const keyToTitle = PropertyToTitle;
//   {
//   code: 'Code',
//   regularSuppCode: 'Supplier',
//   suppCode: 'Supplier Stock Code',
//   desc: 'Description',
//   dep: 'Department',
//   subDep: 'Sub Department',
//   sellPriIncl1: 'Price',
//   sellPriExcl1: 'Price (excl)',
//   gp1: 'GP',
//   nominalGP: 'Nominal GP',
//   recommendedGP: 'Recommended GP',
//   lastSold: 'Date Last Sold',
//   lastPurchase: 'Date Last Purchased',
//   onHand: 'On Hand',
//   latestCost: 'Latest Cost',
//   lineColourCode: 'Line Colour',
//   unitsYear: 'Units 12 Months',
//   packSize: 'Pack Size',
//   onHoldCode: 'On Hold Code',
//   suppUsedLastDate2: 'Prev GRV Date',
//   suppUsedLastDate1: 'GRV Date',
//   ordLvl: 'Order Level',
//   prvSellingPri: 'Prev Sell Price',
//   prvSellingPriDate: 'Prev Sell Price Date',
//   purchaseOrder: 'Purchase Orders',
//   binL: 'Bin Location',
//   barcode: 'Barcode',
//   dlvColl: 'DelCol Outstanding',
//   genCode: 'General Code',
//   excludeSellingValue: 'Exclude Sell Value',
// };

export const keyToType: { [k in keyof StockItem]?: 'string' | 'number' | 'date' } = {
  regularSuppCode: 'string',
  suppCode: 'string',
  desc: 'string',
  alternateDescription: 'string',
  dep: 'string',
  subDep: 'string',
  sellPriIncl1: 'number',
  sellPriExcl1: 'number',
  sellPrice2: 'number',
  sellPriceIncluding2: 'number',
  sellPrice3: 'number',
  sellPriceIncluding3: 'number',
  sellPrice4: 'number',
  sellPriceIncluding4: 'number',
  sellPrice5: 'number',
  sellPriceIncluding5: 'number',
  sellPrice6: 'number',
  sellPriceIncluding6: 'number',
  sellPrice7: 'number',
  sellPriceIncluding7: 'number',
  sellPrice8: 'number',
  sellPriceIncluding8: 'number',
  sellPrice9: 'number',
  sellPriceIncluding9: 'number',
  sellPrice10: 'number',
  sellPriceIncluding10: 'number',
  gp1: 'number',
  recommendedGP: 'number',
  lastMoved: 'date',
  lastSold: 'date',
  lastPurchase: 'date',
  onHand: 'number',
  latestCost: 'number',
  stockValue: 'number',
  lineColourCode: 'number',
  unitsYear: 'number',
  packSize: 'number',
  onHoldCode: 'number',
  suppUsedLastDate5: 'date',
  suppUsedLastDate4: 'date',
  suppUsedLastDate3: 'date',
  suppUsedLastDate2: 'date',
  suppUsedLastDate1: 'date',
  lastStockTake: 'date',
  modified: 'date',
  created: 'date',
  ordLvl: 'number',
  // orderQuantity: 'number',
  orderQty: 'number',
  prvSellingPri: 'number',
  prvSellingPriDate: 'date',
  purchaseOrder: 'number',
  binL: 'string',
};


export const bitEncode = (num: number, length = 16): (1 | 0)[] => {
  const bits: number[] = [];

  for (let i = length - 1; i >= 0; i--) {
    bits.push(Math.floor((num / Math.pow(2, i)) % 2));
  }
  return bits as (1 | 0)[];
};

export const bitDecode = (bits: boolean[]): number => {
  let num = 0;
  bits.forEach((value, index) => {
    num += (value ? Math.pow(2, bits.length - index - 1) : 0);
  });
  return num;
};

/* -------------------------------------------------- TESTING SHIZ -------------------------------------------------- */

export interface FormPost {
  body: string;
  title: string;
  ts: Date;
  displayName?: string;
  id?: string;
  imgs?: ImageObj[];
  import?: boolean;
  poll?: FormPostPoll;
  subHeader?: string;
  tags?: string[];
}

export interface ImageObj {
  name: string;
  thumb: string;
  desc?: string;
  thumbUrl?: string;
  url?: string;
}

export interface FormPostPoll {
  options: {
    text: string[];
    value: (string | number)[];
  };
  disabled?: boolean;
  expire?: Date;
  formPostID?: string;
  multi?: boolean;
  text?: string;
  votes?: Record<string | number, number>;
}

export type LinkedDepartmentsLinkType = 'dep' | 'sub';

export interface LinkedDepartments {
  idxLookUp: Record<string, number>;

  [index: number]: {
    dep: Record<string, Record<number, string[]>>;
    sub: Record<string, Record<number, string[]>>;
  };

  sIdLookUp: Record<number, string>;
}
